import { TestParserOptions } from '../../options/testParserOptions.js';
import { ObjectField, Test, TestType } from '../../parser/models/all.js';
import {
  getPureFunctionCases,
  hasPureFunctionCases
} from '../../parser/utils/testCaseUtils.js';
import { hasOnly } from '../../parser/utils/testOnlyUtils.js';
import { getTitle, hasTitle } from '../../parser/utils/testTitleUtils.js';
import { PreProcessContext } from '../models/preProcessContext.js';

/**
 * Processor for identifying and creating `Test` objects from functions.
 * It handles standard functions, functions with custom titles, and functions marked with 'only'.
 * 
 * @param field - The object field to process.
 * @param index - Field index in the collection.
 * @param context - The pre-processing context.
 * @returns True if the field was processed as a function test.
 */
export function processFunction(field: ObjectField, index: number, context: PreProcessContext): boolean {
  let { name, value } = field;

  if (field.isFunction) {
    const options = context.options;
    const isFunctionBuiltIn = context.isBuiltInFunction(name);
    const isPureFunctionWithOnly = hasOnly(value, options);
    const isPureFunctionWithTitle = hasTitle(value, options);
    const isPureFunctionWithCases = hasPureFunctionCases(value, options);

    let type = TestType.FUNCTION
    type |= isFunctionBuiltIn ? TestType.BUILTIN : 0;
    type |= isPureFunctionWithTitle ? TestType.FUNCTION_WITH_TITLE : 0;
    type |= isPureFunctionWithOnly ? TestType.FUNCTION_WITH_ONLY : 0;
    type |= isPureFunctionWithCases ? TestType.FUNCTION_WITH_CASES : 0;

    const testCases = isPureFunctionWithCases
      ? getPureFunctionCases(value, options)
      : [];

    const boundFn = isPureFunctionWithCases
      ? value
      : getTestValueAsFunction(
        field.groupName,
        value,
        options
      );

    context.tests.push(
      new Test(
        field.groupName,
        // name
        isPureFunctionWithTitle
          ? getTitle(value, options) as string
          : name,
        // value
        boundFn,
        new TestType(type),
        testCases,
        // only expression
        isPureFunctionWithOnly ? value.only || value["__test.only__"] : ""
      )
    );

    return true;
  }

  return false;
}

/**
 * Wraps a test function to inject context via `testContextLookup`.
 * It preserves the function length to maintain compatibility with test runners that inspect it.
 * 
 * @param groupName - The test group name.
 * @param testFn - The original test function.
 * @param options - Parser options.
 * @returns A wrapped function that applies the group context.
 */
function getTestValueAsFunction(groupName: string, testFn: Function, options: TestParserOptions): Function {
  // some test runners inspect the test fn length
  // when the length is > 0 they then pass their utility args to the test fn
  // i.e. a done() callback
  if (testFn.length === 0) {
    return function(this: any) {
      const thisArg = options.testContextLookup(groupName);
      return testFn.call(thisArg);
    }
  } else if (testFn.length === 1) {
    // ensures the length of the function is at least 1
    return function(this: any, first: any, ...args: any[]) {
      const thisArg = options.testContextLookup(groupName);
      return testFn.call(thisArg, first, ...args);
    }
  } else {
    // ensures the length of the function is at least 2
    return function(this: any, first: any, second: any, ...args: any[]) {
      const thisArg = options.testContextLookup(groupName);
      return testFn.call(thisArg, first, second, ...args);
    }
  }
}
